import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { CheckCircleIcon, LockOpenIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { SUBMIT_EOD_SESSION, REOPEN_EOD_SESSION } from '../../../graphql/production-mutations'
import toast from 'react-hot-toast'

interface EodSession {
  id: number
  date: string
  status: 'draft' | 'submitted'
  submitted_at?: string | null
}

interface SessionHeaderProps {
  date: string
  session: EodSession | null
  sessionLoading: boolean
  canLog: boolean
  onDateChange: (date: string) => void
  onSessionUpdate: (updated: Partial<EodSession>) => void
}

const todayString = () => new Date().toISOString().slice(0, 10)

const SessionHeader = ({
  date,
  session,
  sessionLoading,
  canLog,
  onDateChange,
  onSessionUpdate,
}: SessionHeaderProps) => {
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false)

  const [submitSession, { loading: submitting }] = useMutation(SUBMIT_EOD_SESSION, {
    onCompleted: (data) => {
      onSessionUpdate(data.submitEodSession)
      toast.success('End-of-shift session submitted')
      setSubmitConfirmOpen(false)
    },
    onError: (e) => toast.error(e.message),
  })

  const [reopenSession, { loading: reopening }] = useMutation(REOPEN_EOD_SESSION, {
    onCompleted: (data) => {
      onSessionUpdate(data.reopenEodSession)
      toast.success('Session reopened')
    },
    onError: (e) => toast.error(e.message),
  })

  const isDraft = session?.status === 'draft'
  const isSubmitted = session?.status === 'submitted'

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            value={date}
            max={todayString()}
            onChange={(e) => onDateChange(e.target.value)}
            className="input text-sm py-1.5"
          />
        </div>

        <div className="flex-1" />

        {sessionLoading && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
            Loading…
          </span>
        )}
        {!sessionLoading && session && (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
            isSubmitted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {isSubmitted ? (
              <><CheckCircleIcon className="h-3.5 w-3.5" /> Submitted</>
            ) : (
              <><span className="h-2 w-2 rounded-full bg-yellow-400" /> Draft</>
            )}
          </span>
        )}

        {canLog && session && isDraft && (
          <button
            onClick={() => setSubmitConfirmOpen(true)}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Submit
          </button>
        )}
        {canLog && session && isSubmitted && (
          <button
            onClick={() => reopenSession({ variables: { id: session.id } })}
            disabled={reopening}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <LockOpenIcon className="h-4 w-4" />
            Reopen
          </button>
        )}
      </div>

      {submitConfirmOpen && session && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Submit end-of-shift?</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Check the summary step for any recipes with negative variance before submitting.
                  You can reopen this session afterwards if corrections are needed.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setSubmitConfirmOpen(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => submitSession({ variables: { id: session.id } })}
                disabled={submitting}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SessionHeader
